import type { ReactNode } from "react";
import { ApiProblemError } from "../lib/authManager";
import { passwordChecklist } from "../lib/authForm";
import { initialOf } from "../lib/profile";
import { CheckIcon, ChevronDownIcon } from "./icons";

/** A settings-sheet field. The auth screens have their own (auth-card.tsx AuthField). */
export function Field(props: {
  label: string;
  htmlFor: string;
  hint?: ReactNode;
  error?: string;
  /** False for a submit's batch of lines (lib/authForm ShownError.alert):
   * the focus moves to the first field, which reads its own line through
   * aria-describedby, instead of every line being announced at once. */
  alert?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="mb-4">
      <label
        htmlFor={props.htmlFor}
        className="mb-1.5 block text-[13px] font-medium text-[var(--rago-text-2)]"
      >
        {props.label}
      </label>
      {props.children}
      {props.error ? (
        <p
          id={`${props.htmlFor}-error`}
          role={props.alert === false ? undefined : "alert"}
          className="mt-1.5 text-xs text-[var(--rago-error)]"
        >
          {props.error}
        </p>
      ) : props.hint ? (
        <p className="mt-1.5 text-xs text-[var(--rago-text-3)]">{props.hint}</p>
      ) : null}
    </div>
  );
}

/** A small status pill. Replaces the per-page AvailabilityChip copies. */
export function Chip(props: {
  tone?: "ok" | "muted" | "accent" | "warn";
  /** "md": the profile header's chips — 12px text with a little more air. */
  size?: "md";
  children: ReactNode;
}) {
  const tone = props.tone ?? "muted";
  const size = props.size === "md" ? "px-2.5 py-1 text-xs" : "px-2 py-0.5 text-[11px]";
  const cls = {
    ok: "border-[rgba(62,207,142,0.4)] text-[var(--rago-ok)]",
    accent: "border-[var(--rago-accent)] text-[var(--rago-accent)]",
    warn: "border-[rgba(245,220,74,0.45)] text-[var(--rago-warn)]",
    muted: "border-[var(--rago-line-2)] text-[var(--rago-text-3)]",
  }[tone];
  return (
    <span className={`inline-flex select-none items-center rounded-full border ${size} ${cls}`}>
      {props.children}
    </span>
  );
}

/** An error in words: a problem response's detail (else its title), an
 * Error's own message, else a plain line. What ErrorNote shows, for the
 * places that take the words themselves (the unsaved pill's note). */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiProblemError) return error.problem.detail ?? error.problem.title;
  if (error instanceof Error) return error.message;
  return "Something went wrong";
}

/** The form-level error line. `children` replaces the derived message when a
 * screen has better words for the problem (the auth screens, via
 * lib/authForm describeAuthError) and may carry a link. */
export function ErrorNote(props: { error: unknown; children?: ReactNode }) {
  if (!props.error) return null;
  const message = props.children ?? errorMessage(props.error);
  return (
    <div
      role="alert"
      className="mb-4 rounded-[var(--rago-radius-m)] border border-[rgba(255,92,92,0.35)] bg-[rgba(255,92,92,0.08)] px-3.5 py-2.5 text-sm text-[var(--rago-error)]"
    >
      {message}
    </div>
  );
}

/** The password rules under a new-password well (lib/authForm
 * passwordChecklist): dot rows, grey until a rule is met, lit once it is.
 * Always shown, so the rule is known before typing. Two columns at most (the
 * wide settings sheet too), one where "A special character" would wrap. The
 * auth screens drop the heading (Claude Design 4c); Settings keeps it. */
export function PasswordChecklist(props: { password: string; heading?: boolean }) {
  const items = passwordChecklist(props.password);
  return (
    <div className="mt-3.5" aria-live="polite">
      {props.heading === false ? null : (
        <p className="mb-2 text-[14px] font-semibold text-[var(--rago-text-2)]">
          Password requirements
        </p>
      )}
      <ul
        role="list"
        className="grid grid-cols-[repeat(auto-fit,minmax(max(9.5rem,calc(50%_-_6px)),1fr))] gap-x-3 gap-y-2"
      >
        {items.map((item) => (
          <li
            key={item.key}
            className={`flex items-center gap-2.5 text-[13.5px] leading-[1.2] transition-colors ${
              item.ok ? "text-[var(--rago-text-1)]" : "text-[var(--rago-text-dim)]"
            }`}
          >
            <span
              aria-hidden
              className="h-2 w-2 shrink-0 rounded-full transition-colors"
              style={{
                background: item.ok ? "var(--rago-ok)" : "var(--rago-line-2)",
                boxShadow: item.ok ? "0 0 0 3px rgba(62,207,142,0.18)" : "none",
              }}
            />
            {item.label}
            <span className="sr-only">{item.ok ? ": met" : ": not met"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SuccessNote(props: { children: ReactNode }) {
  return (
    <div className="mb-4 rounded-[var(--rago-radius-m)] border border-[rgba(62,207,142,0.35)] bg-[rgba(62,207,142,0.08)] px-3.5 py-2.5 text-sm text-[var(--rago-ok)]">
      {props.children}
    </div>
  );
}

/** The one loading glyph (Plex's grammar): an arc that spins while its
 * length breathes, round caps, currentColor. 16px inline in buttons and
 * labels, 28px page-level in amber (LoadingState), 40px in the player.
 * Decorative on its own — the wrapper carries role="status" where the
 * spinner is the whole message. Motion lives in app.css (.rago-spinner). */
export function Spinner(props: { size?: number }) {
  const size = props.size ?? 16;
  return (
    <svg
      aria-hidden
      className="rago-spinner"
      viewBox="0 0 24 24"
      width={size}
      height={size}
    >
      <circle
        className="rago-spinner-arc"
        cx="12"
        cy="12"
        r="10"
        fill="none"
        stroke="currentColor"
        // Thinner as it grows: 2.75 viewBox units ≈ 1.8px at 16, 2.25 ≈ 2.6px at 28.
        strokeWidth={size <= 20 ? 2.75 : 2.25}
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Pill tab switcher. Active is neutral on purpose — amber stays reserved
 * for nav-active and primary actions, so two amber systems never fight. */
export function SegmentedTabs<T extends string>(props: {
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
  label?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={props.label}
      className="inline-flex gap-0.5 rounded-full border border-[var(--rago-line-1)] bg-white/5 p-1"
    >
      {props.options.map((o) => {
        const active = o.value === props.value;
        return (
          <button
            key={o.value}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => props.onChange(o.value)}
            className={`h-8 cursor-pointer rounded-full px-3.5 text-[13px] font-medium transition-colors focus-visible:ring-2 focus-visible:ring-[var(--rago-ring)] focus-visible:outline-none ${
              active
                ? "bg-[var(--rago-bg-3)] text-[var(--rago-text-1)]"
                : "text-[var(--rago-text-3)] hover:text-[var(--rago-text-2)]"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * One choice out of a few, as pills in a track with the chosen one filled
 * amber (Settings › Trakt's sync interval; Kenny's render). Real radios
 * under the pills: the group is one tab stop and the arrow keys move the
 * choice, as in any radio group. 40px tall around 30px pills: a taller
 * track read as bulky, and a pill that nearly filled it as too much amber
 * (Kenny). SegmentedTabs above is the other thing — tabs, with a
 * neutral pill.
 */
export function SegmentedChoice<T extends string>(props: {
  /** The radios' shared name: one per group on a page. */
  name: string;
  /** Ids of the group's title and of the line that describes the choice. */
  labelledBy: string;
  describedBy?: string;
  /** null: what is stored is none of the options, and none is filled. */
  value: T | null;
  options: readonly { value: T; label: string }[];
  /** A change is being saved: further ones wait (the radios stay
   * focusable — `disabled` would drop the focus mid-keystroke). */
  busy?: boolean;
  onChange: (value: T) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-labelledby={props.labelledBy}
      aria-describedby={props.describedBy}
      className={`flex rounded-full border border-[var(--rago-line-1)] bg-black/30 p-1 ${
        props.busy ? "opacity-70" : ""
      }`}
    >
      {props.options.map((o) => (
        // `relative`: the sr-only radio stays anchored to its pill (Checkbox on why).
        <label key={o.value} className="relative flex-1 cursor-pointer">
          <input
            type="radio"
            name={props.name}
            value={o.value}
            className="peer sr-only"
            checked={o.value === props.value}
            onChange={() => {
              if (!props.busy) props.onChange(o.value);
            }}
          />
          <span className="flex h-[30px] items-center justify-center rounded-full px-3.5 text-[13px] font-medium whitespace-nowrap text-[var(--rago-text-2)] transition-colors select-none peer-checked:bg-[var(--rago-accent)] peer-checked:font-semibold peer-checked:text-[var(--rago-accent-ink)] peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[var(--rago-ring)] max-[430px]:px-1.5">
            {o.label}
          </span>
        </label>
      ))}
    </div>
  );
}

/** Native select in the app's input skin (the chevron is ours because
 * `appearance-none` drops the platform one). */
export function Select(props: {
  id?: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <div className="relative">
      <select
        id={props.id}
        className="rago-input appearance-none pr-9 disabled:cursor-not-allowed disabled:opacity-60"
        value={props.value}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.value)}
      >
        {props.children}
      </select>
      <ChevronDownIcon
        size={16}
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--rago-text-3)]"
      />
    </div>
  );
}

/** Checkbox row: a real input for a11y, an accent-filled box for looks. */
export function Checkbox(props: {
  id?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  /** A sentence, or a status line (chip + text) rendered inline. */
  hint?: ReactNode;
}) {
  return (
    <label
      htmlFor={props.id}
      // `relative` anchors the sr-only (absolutely positioned) input to this
      // row: without it the input's containing block is the nearest
      // positioned ancestor — a modal panel — and focusing it (a click on
      // the label, a Tab) scrolls that panel to a phantom spot below its
      // content, shifting the whole dialog up.
      className={`relative flex gap-3 ${props.disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <input
        id={props.id}
        type="checkbox"
        className="sr-only"
        checked={props.checked}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.checked)}
      />
      <span
        aria-hidden
        className={`mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[var(--rago-radius-s)] border transition-colors ${
          props.checked
            ? "border-[var(--rago-accent)] bg-[var(--rago-accent)] text-[var(--rago-accent-ink)]"
            : "border-[var(--rago-line-2)]"
        }`}
      >
        {props.checked ? <CheckIcon size={13} /> : null}
      </span>
      <span className="min-w-0">
        <span className="block text-sm text-[var(--rago-text-1)]">{props.label}</span>
        {props.hint ? (
          <span className="mt-0.5 block text-xs text-[var(--rago-text-3)]">{props.hint}</span>
        ) : null}
      </span>
    </label>
  );
}

/**
 * Switch: an option that applies the moment it is flipped, at the right
 * edge of its row (Settings › Trakt). A real checkbox in the switch role
 * under a track and a thumb; the row's <label htmlFor> names it and its
 * sentence describes it.
 */
export function Switch(props: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  /** Id of the sentence under the row's title. */
  describedBy?: string;
}) {
  return (
    // `relative`: the sr-only input stays anchored to the switch (Checkbox on why).
    <label
      className={`relative inline-flex shrink-0 ${
        props.disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
      }`}
    >
      <input
        id={props.id}
        type="checkbox"
        role="switch"
        className="peer sr-only"
        aria-describedby={props.describedBy}
        checked={props.checked}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.checked)}
      />
      <span
        aria-hidden
        className={`flex h-6 w-[42px] items-center rounded-full border p-[2px] transition-colors peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[var(--rago-ring)] ${
          props.checked
            ? "border-[var(--rago-accent)] bg-[var(--rago-accent)]"
            : "border-[var(--rago-line-2)] bg-white/10"
        }`}
      >
        <span
          className={`h-[18px] w-[18px] rounded-full shadow-[0_1px_3px_rgba(0,0,0,0.45)] transition-transform ${
            props.checked ? "translate-x-[18px] bg-white" : "bg-[var(--rago-text-2)]"
          }`}
        />
      </span>
    </label>
  );
}

/**
 * The accent-initial avatar (the account-menu idiom): a person's or
 * server owner's first letter in a soft accent disc. "sm" is the list-row
 * size (Settings › Sharing, 40px); "md" the card size (Discovery's
 * watch-from cards); "lg" a person's own page. `dashed`: somebody who is
 * not there yet (an invite still waiting) — an outline, no fill.
 */
export function InitialBadge({
  name,
  size = "sm",
  dashed = false,
}: {
  name: string;
  size?: "sm" | "md" | "lg";
  dashed?: boolean;
}) {
  const box = {
    sm: "h-10 w-10 text-[15px]",
    md: "h-11 w-11 text-base",
    lg: "h-[60px] w-[60px] text-[23px]",
  }[size];
  const look = dashed
    ? "border-[1.5px] border-dashed border-white/35 text-[var(--rago-text-3)]"
    : "bg-[var(--rago-accent-soft)] text-[var(--rago-accent-strong)]";
  return (
    <span
      aria-hidden
      className={`flex shrink-0 select-none items-center justify-center rounded-full font-semibold ${box} ${look}`}
    >
      {initialOf(name)}
    </span>
  );
}

/** Small presence dot: green when online, muted otherwise. */
export function StatusDot({ online }: { online: boolean }) {
  return (
    <span
      aria-hidden
      className={`mr-1.5 inline-block h-2 w-2 rounded-full align-middle ${
        online ? "bg-[var(--rago-ok)]" : "bg-[var(--rago-text-3)]"
      }`}
    />
  );
}

/** Plex-style read-only settings row: uppercase label over the value. */
export function InfoRow(props: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <p className="rago-caps">
        {props.label}
      </p>
      <p className="mt-1 text-sm text-[var(--rago-text-1)]">{props.children}</p>
      {props.hint ? <p className="mt-0.5 text-xs text-[var(--rago-text-3)]">{props.hint}</p> : null}
    </div>
  );
}
